import {global} from "../../../appContext";
import {IExportTemplate, IHistory, IMessageData, IRouteDefinitionParams, ITableColumn} from "../../../index";
import {_} from 'lodash';
import {ObjectTypeId} from "../../../constants";
import {storeObservable} from "../../../util/sessionstore";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import * as ko from "knockout";
import {getObjectDefinition} from "../../../util/misc";
import {nsRoute} from "../../../util/route";
import {ButtonManager} from "../../../util/ButtonManager";
import {getCancelButton, getHash} from "../../../constants/RouteDefinitions";
import {PLEASE_WAIT_MESSAGE} from "../../../constants/Language";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import tpl_rowHtml from './tpl_row.html?raw';
import tpl_resultsHtml from './tpl_results.html?raw';

export let tplRow: string = tpl_rowHtml;
export let tplResults: string = tpl_resultsHtml;

interface IExportActionGrouped {
    data: IHistory[]
    label: string
    pages: KnockoutObservable<number>
    toggle: KnockoutObservable<boolean>
}

interface IFilterGroupOption {
    label: string
    operator: LogicOperator
}

enum LogicOperator {
    NotEqual,
    Equal,
    Less,
    LessOrEqual,
    Greater,
    GreaterOrEqual,
}

export abstract class AbstractExport extends ParsedRouteDataDecorator {
    tplRow: string = tplRow;
    tplResults: string = tplResults;

    isReady: KnockoutObservable<boolean> = ko.observable(false);
    isDataReady: KnockoutObservable<boolean> = ko.observable(false);

    messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    messageDataResult: KnockoutObservable<IMessageData | null> = ko.observable(null);

    exportTemplates!: IExportTemplate[];
    exportActions: KnockoutObservableArray<IHistory> = ko.observableArray<IHistory>([]);

    sortcol: KnockoutObservable<string> = ko.observable("newvalue");
    asc: KnockoutObservable<boolean> = ko.observable(true);
    groupBy: KnockoutObservable<ITableColumn | null> = ko.observable(null);

    showPreview: KnockoutObservable<boolean> = ko.observable(false);
    showResults: KnockoutObservable<boolean> = ko.observable(false);

    percentComplete: KnockoutObservable<number | null> = ko.observable(null);
    selectedObjectTypeId: KnockoutObservable<number | null> = ko.observable(null);
    isRequesting: KnockoutObservable<boolean> = ko.observable(false);

    // whatever the user has typed
    filterKey: KnockoutObservable<string> = ko.observable('');

    // page navigation
    hitsPerPage: KnockoutObservable<number> = storeObservable("num.search.results");

    // the unfiltered total
    allTotal: KnockoutObservable<number | null> = ko.observable(null);
    private filteredTotal: KnockoutComputed<number> = ko.pureComputed((): number => {
        if (this.groupBy()) {
            return this.dataProcessedGrouped().length;
        } else {
            return this.dataProcessed().length;
        }
    });
    private currentPageNumber: KnockoutObservable<number> = ko.observable(0); // should be null
    private totalPageNumber: KnockoutComputed<number> = ko.pureComputed((): number => Math.ceil(this.filteredTotal() / this.hitsPerPage()));

    protected data: KnockoutComputed<IHistory[] | IHistory[][]> = ko.pureComputed(() => {
        if (this.groupBy()) {
            return this.dataProcessedGrouped();
        } else {
            return this.dataProcessed();
        }
    })

    filterGroupTypes: IFilterGroupOption[] = [
        {label: '≠ ((different))', operator: LogicOperator.NotEqual},
        {label: '= ((equal))', operator: LogicOperator.Equal},
        {label: '< ((less))', operator: LogicOperator.Less},
        {label: '≤ ((less or equal))', operator: LogicOperator.LessOrEqual},
        {label: '> ((greater))', operator: LogicOperator.Greater},
        {label: '≥ ((greater or equal))', operator: LogicOperator.GreaterOrEqual},
    ];

    filterGroupType: KnockoutObservable<IFilterGroupOption | null> = ko.observable(null); // not equal
    filterGroupValue: KnockoutObservable<number> = ko.observable(0);

    TABLE: ITableColumn[] = [
        {
            label: "id",
            key: "userid",
            sort: true
        },
        {
            label: "type",
            key: "entrytype",
            sort: true
        },
        {
            label: "attribute",
            key: "attributename",
            sort: true
        },
        {
            label: "new value",
            key: "newvalue",
            sort: true
        },
        {
            label: 'object type',
            key: 'objecttypeid',
            sort: true,
        }
    ];

    protected reqSearchExportTemplate = global.getPromiseRequest("searchexporttemplate");
    protected kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    protected readonly pageButtons: ButtonManager = new ButtonManager()

    constructor(params: IRouteDefinitionParams) {
        super(params);
        [
            this.hitsPerPage,
            this.filterKey,
            this.sortcol,
            this.groupBy,
            this.filterGroupType,
            this.filterGroupValue,
        ].forEach((obs: KnockoutObservable<any>) => this.kosub.subscribe(obs, () => this.currentPageNumber(0)));
        this.kosub.subscribe(this.selectedObjectTypeId, () => {
            this.showPreview(false);
            this.showResults(false);
        })

        this.pageButtons.add(getCancelButton())
    }

    protected abstract initRequest(): void;

    onPreviewClick = this.warnBeforeExecute.bind(this, true);
    onExecuteClick = this.warnBeforeExecute.bind(this, false);

    protected abstract warnBeforeExecute(preview: boolean): void

    protected execute(preview: boolean) {
        this.allTotal(null);
        this.exportActions([]);
        this.percentComplete(0);
        this.messageDataResult({message: PLEASE_WAIT_MESSAGE});
        this.exportActions([]);
        this.showPreview(preview);
        this.showResults(!preview);
        this.isDataReady(false);
        this.isRequesting(true);
    };

    // ------------------------------------------------ RESULTS ------------------------------------------------

    dataPaged = ko.pureComputed((): IHistory[] => {
        const data = this.dataProcessed();
        const start = this.currentPageNumber() * this.hitsPerPage();
        const end = start + Math.min(data.length, this.hitsPerPage());
        return data.slice(start, end);
    });

    dataProcessed(): IHistory[] {
        let ret = this.exportActions();
        ret = this.getDataFiltered(ret);
        ret = this.getDataSorted(ret);
        return ret;
    }

    private getDataFiltered(data: IHistory[]): IHistory[] {
        const key = this.filterKey().toLowerCase();
        return data.filter((obj: IHistory) => {
            return this.TABLE.some((col: ITableColumn) => {
                const haystack = String((obj as any)[col.key]).toLowerCase();
                return haystack.indexOf(key) > -1;
            });
        });
    }

    private getDataSorted(data: IHistory[]): IHistory[] {
        let ret: IHistory[];
        ret = _.sortBy(data, [this.sortcol(), 'id', 'userid', 'entrytype', 'attributename', 'newvalue', 'objecttypeid']);
        return this.asc() ? ret : ret.reverse();
    }

    dataPagedGroup = ko.pureComputed((): IExportActionGrouped[] => {
        const groupBy = this.groupBy();
        if (!groupBy) {
            return [];
        }
        let data = this.dataProcessedGrouped();
        const start = this.currentPageNumber() * this.hitsPerPage();
        const end = start + Math.min(data.length, this.hitsPerPage());
        data = data.slice(start, end);
        const dataPaged: IExportActionGrouped[] = data.map((data: IHistory[]): IExportActionGrouped => {
            return {
                data: data,
                label: (data[0] as any)[groupBy.key] || "—",
                pages: ko.observable(1),
                toggle: ko.observable(false),
            }
        });
        return dataPaged;
    });

    dataProcessedGrouped = ko.pureComputed((): IHistory[][] => {
        const groupBy = this.groupBy();
        if (!groupBy) {
            return [];
        }
        let data: IHistory[] = this.dataProcessed();
        let dataGrouped: IHistory[][] = _.toArray(_.groupBy(data, groupBy.key));
        const filterGroupType = this.filterGroupType();
        if (filterGroupType) {
            dataGrouped = dataGrouped.filter((group: IHistory[]) => {
                const len = group.length;
                const val = this.filterGroupValue();
                switch (filterGroupType.operator) {
                    case LogicOperator.NotEqual:
                        return len != val;
                    case LogicOperator.Equal:
                        return len == val;
                    case LogicOperator.Less:
                        return len < val;
                    case LogicOperator.LessOrEqual:
                        return len <= val;
                    case LogicOperator.Greater:
                        return len > val;
                    case LogicOperator.GreaterOrEqual:
                        return len >= val;
                }
            });
        }
        if (this.asc()) {
            return dataGrouped;
        } else {
            return dataGrouped.reverse();
        }

    });

    getHref(item: IHistory): string {
        const routeId = getObjectDefinition(item.objecttypeid)?.routeDetails;
        return routeId ? (getHash(routeId, {id: item.userid}) ?? '') : '';
    }

    // ------------------------------------------------ NAVIGATION ------------------------------------------------
    onPreviousPageClick() {
        this.currentPageNumber(this.currentPageNumber() - 1);
    }

    isPreviousPageEnabled() {
        return !!this.currentPageNumber();
    }

    onNextPageClick() {
        this.currentPageNumber(this.currentPageNumber() + 1);
    }

    isNextPageEnabled() {
        return this.currentPageNumber() < this.totalPageNumber() - 1;
    }

    getPagingText = ko.pureComputed((): string => {
        return `Side ${this.currentPageNumber() + 1} av ${this.totalPageNumber()}`;
    });

    onGroupShowMoreClick(data: IExportActionGrouped) {
        data.pages(data.pages() + 1);
    }

    onGroupShowAllClick(data: IExportActionGrouped) {
        data.pages(Math.ceil(data.data.length / this.hitsPerPage()));
    }

    groupHasMore(data: IExportActionGrouped): boolean {
        return data.pages() * this.hitsPerPage() < data.data.length;
    }

    // ------------------------------------------------ UI HELPERS ------------------------------------------------

    getObjectTypeCssClass = (objectTypeId: ObjectTypeId) => getObjectDefinition(objectTypeId)?.icon ?? '';
    getObjectTypeTitle = (objectTypeId: ObjectTypeId) => getObjectDefinition(objectTypeId)?.singular ?? '';

    override dispose() {
        super.dispose();
        this.kosub.unsuscribeAll();
    }

}
